import { Store } from "@ngrx/store";
import { DatePipe } from "@angular/common";
import { FormBuilder, FormGroup } from "@angular/forms";
import { Component, ElementRef, inject, Input, TemplateRef, ViewChild } from "@angular/core";
import { PopupType } from "src/app/shared/enums/popup-types";
import { showMessage } from "src/app/shared/utils/toast.popup";
import { Bill, BillStatus } from "src/app/shared/types/contract-types";
import { BillService } from "src/app/shared/services/bill.service";
import { ContractService } from "src/app/shared/services/contract.service";
import { DialogRef, DialogService } from "@ngneat/dialog";
import { TranslateService } from "@ngx-translate/core";
import { FlatpickrDefaultsInterface } from "angularx-flatpickr";
import { MessagePortalComponent } from "src/app/portal/portal";
import { PositioningService } from "src/app/shared/services/positioning.service";
import { toggleAnimation } from "src/app/shared/animations";
import dayjs from "dayjs/esm";
import customParseFormat from "dayjs/esm/plugin/customParseFormat";

dayjs.extend(customParseFormat);

@Component({
  selector: "app-bills",
  templateUrl: "./contract-bills.component.html",
  standalone: false,
  animations: [toggleAnimation],
})
export class ContractBillsComponent {
  createPriceForm!: FormGroup;
  sendMailForm!: FormGroup;
  dateArray: any;
  wrongStartDateInput: boolean = false;
  wrongEndDateInput: boolean = false;
  remainingTime!: string;
  packageTitle!: string;
  daysLeft!: number;
  isLoading: boolean = false;
  totalDaysLength!: number;
  packagePrice: number = 0;
  items: Bill[] = [];
  isLoadingDelete = false;
  billToDelete?: number;

  datePickerOptions: FlatpickrDefaultsInterface = {
    altInput: true, // Optional: if you want a "prettier" displayed format
    altFormat: "d.m.Y", // Optional: visible format if altInput is true
  };

  @ViewChild("addTaskModalTemplate") addTaskModalTemplate!: TemplateRef<unknown>;
  @ViewChild("dropdownPlaceholder") dropdownPlaceholder!: ElementRef;
  @ViewChild("deleteConfirmationTemplate") deleteConfirmationTemplate!: TemplateRef<unknown>;
  @ViewChild(MessagePortalComponent) messagePortalComponent!: MessagePortalComponent;

  @Input() contractId: any;

  cols: any[] = [];

  options = [
    { value: "RSD", label: "RSD" },
    { value: "BAM", label: "BAM" },
    { value: "EUR", label: "EUR" },
  ];

  addTaskModal!: DialogRef;
  deleteModal!: DialogRef;
  private dialog = inject(DialogService);

  constructor(
    public contractService: ContractService,
    public billService: BillService,
    public storeData: Store<any>,
    public fb: FormBuilder,
    public datePipe: DatePipe,
    private translate: TranslateService,
    private positioningService: PositioningService,
  ) {
    this.updateColumnTranslations();
  }

  ngOnInit(): void {
    this.updateColumnTranslations();
    this.loadData();

    this.translate.onLangChange.subscribe(() => {
      this.updateColumnTranslations();
    });
  }

  private updateColumnTranslations(): void {
    this.cols = [
      { field: "id", title: this.translate.instant("bills.id") },
      {
        field: "startDate",
        title: this.translate.instant("bills.start_date"),
      },
      { field: "endDate", title: this.translate.instant("bills.end_date") },
      {
        field: "createdDate",
        title: this.translate.instant("bills.created_date"),
      },
      {
        field: "updatedDate",
        title: this.translate.instant("bills.updated_date"),
      },
      { field: "deliveryStatus", title: this.translate.instant("billing.delivery_status") },
      { field: "status", title: this.translate.instant("bills.status") },
      {
        field: "actions",
        title: this.translate.instant("bills.actions"),
        sort: false,
        headerClass: "justify-center",
      },
    ];
  }

  private initializeForms(date: Date) {
    this.sendMailForm = this.fb.group({
      mail: ["eminhamzagic7@gmail.com"],
      message: ["FAKTURA"],
    });

    this.createPriceForm = this.fb.group({
      startDate: [date],
      endDate: [date],
      price: [0],
      currencyCode: [this.options[0].value],
      useIBAN: [false],
    });

    this.subscribeToValueChanges();
  }

  loadData() {
    const loadSubscription = this.contractService.getContractById(this.contractId).subscribe((response) => {
      loadSubscription.unsubscribe();
      this.packagePrice = response.package.monthlyPrice;
      this.packageTitle = response.package.title;
      this.items = response.bills!;

      const billWithHighestEndDate = this.getLastDate();
      const lastEndDate = billWithHighestEndDate.endDate;
      this.initializeForms(lastEndDate);

      // load left days
      this.daysLeft = this.calculateDateDifference(new Date(), lastEndDate);
      this.totalDaysLength = this.calculateDateDifference(billWithHighestEndDate.startDate, lastEndDate);
    });
  }

  subscribeToValueChanges() {
    this.createPriceForm.get("endDate")?.valueChanges.subscribe((newValue) => {
      const startDate = this.createPriceForm.get("startDate")?.value;
      this.wrongEndDateInput = false;
      if (new Date(newValue) < new Date(startDate)) {
        showMessage(
          PopupType.Danger,
          this.translate.instant("bills.error_end_date_before"),
        );
        this.wrongEndDateInput = true;
      }
      else {
        this.wrongEndDateInput = false;
        this.countPrice();
      }
    });

    this.createPriceForm.get("startDate")?.valueChanges.subscribe((newValue) => {
      const endDate = this.createPriceForm.get("endDate")?.value;
      this.wrongStartDateInput = false;

      if (new Date(newValue) > new Date(endDate)) {
        showMessage(PopupType.Danger, this.translate.instant("bills.error_start_date_after"));
        this.wrongStartDateInput = true;
      }
      else {
        this.wrongEndDateInput = false;
        this.countPrice();
      }
    });

    this.createPriceForm.get("price")?.valueChanges.subscribe((newValue) => {
      this.onPriceChange(newValue);
    });
  }

  getStatusClass(status: BillStatus): string {
    return status === BillStatus.Paid ? "btn-outline-success" : "btn-outline-danger";
  }

  getDeliveryStatusClass(status: boolean): string {
    return status ? "btn-outline-success" : "btn-outline-danger";
  }

  updateStatus(id: number, status: number) {
    const updateSubscription = this.billService.updateBillStatus(id, status).subscribe(() => {
      updateSubscription.unsubscribe();
      showMessage(PopupType.Success, this.translate.instant("bills.success_status_update"));
      this.loadData();
    });
  }

  getBillStatus(status: BillStatus): string {
    switch (status) {
      case BillStatus.Paid:
        return this.translate.instant("bills.status_paid");
      case BillStatus.Unpaid:
        return this.translate.instant("bills.status_pending");
      default:
        return this.translate.instant("bills.status_unknown");
    }
  }

  getProgressWidth(): string {
    const percentageFilled = ((this.totalDaysLength - this.daysLeft) / this.totalDaysLength) * 100;

    return `${percentageFilled}%`;
  }

  addBill() {
    const transformedBillData = {
      startDate: new Date(this.createPriceForm.value.startDate).toISOString(),
      endDate: new Date(this.createPriceForm.value.endDate).toISOString(),
      price: Number.parseInt(this.createPriceForm.value.price),
      currencyCode: this.createPriceForm.value.currencyCode,
      useIBAN: this.createPriceForm.value.useIBAN,
    };

    if (!this.wrongEndDateInput) {
      this.isLoading = true;
      const addSubscription = this.billService.addBill(this.contractId, transformedBillData).subscribe({
        next: () => {
          addSubscription.unsubscribe();
          this.isLoading = false;
          this.loadData();
          this.addTaskModal.close();
          showMessage(PopupType.Success, this.translate.instant("bills.success_add_bill"));
        },
        error: (err) => {
          addSubscription.unsubscribe();
          this.isLoading = false;
          showMessage(PopupType.Danger, err.error.message);
        },
      });
    }
    else {
      showMessage(PopupType.Danger, this.translate.instant("bills.error_wrong_dates"));
    }
  }

  countPrice() {
    this.wrongEndDateInput = false;
    this.wrongStartDateInput = false;
    const startDate = this.createPriceForm.get("startDate")?.value;
    const endDate = this.createPriceForm.get("endDate")?.value;
    if (startDate && endDate) {
      const diffInDays = this.calculateDateDifference(startDate, endDate);
      this.createPriceForm.patchValue(
        {
          price: ((diffInDays / 30) * this.packagePrice).toFixed(2),
        },
        { emitEvent: false },
      );
    }

    const untilNow = this.calculateDateDifference(this.getLastDate().endDate, startDate);
    const futureMonths = this.calculateDateDifference(startDate, endDate);
    this.remainingTime = `${this.translate.instant(
      "bills.so_far",
    )}: ${this.formatRemainingTime(untilNow)} ${this.translate.instant(
      "bills.elapsed",
    )}, ${this.translate.instant("bills.and")} ${this.formatRemainingTime(
      futureMonths,
    )} ${this.translate.instant("bills.remaining_until")}`;
  }

  getLastDate(): Bill {
    const currentBill: Bill = {
      id: 0,
      filepath: "",
      startDate: new Date(),
      endDate: new Date(),
      createdDate: new Date(),
      updatedDate: new Date(),
      status: BillStatus.Unpaid,
      price: 0,
      isSent: false,
      currencyCode: "",
      useIBAN: false,
    };

    const latestBill = this.items.reduce((latest: any, current: any) => {
      const currentEndDate = dayjs(current.endDate);
      const latestEndDate = latest ? dayjs(latest.endDate) : null;
      return latestEndDate && currentEndDate.isAfter(latestEndDate) ? current : latest;
    }, this.items[0]);

    return latestBill || currentBill;
  }

  onPriceChange(newPrice: number) {
    const startDate = this.createPriceForm.get("startDate")?.value;
    const daysDiff = (newPrice / this.packagePrice) * 30;
    const newEndDate = dayjs(startDate).add(daysDiff, "days").toDate();

    const untilNow = this.calculateDateDifference(
      this.getLastDate().endDate,
      startDate,
    );
    const futureMonths = this.calculateDateDifference(startDate, newEndDate);

    this.remainingTime = `${this.translate.instant(
      "bills.until_now",
    )} ${this.formatRemainingTime(untilNow)} + ${this.formatRemainingTime(
      futureMonths,
    )} ${this.translate.instant("bills.future_months")}`;
  }

  formatRemainingTime(days: number): string {
    const months = Math.floor(days / 30);
    const remainingDays = days % 30;

    const monthText
      = months === 1
        ? this.translate.instant("bills.month")
        : this.translate.instant("bills.months");
    const dayText
      = remainingDays === 1
        ? this.translate.instant("bills.day")
        : this.translate.instant("bills.days");

    const monthPart = months > 0 ? `${months} ${monthText}` : "";
    const dayPart = remainingDays > 0 ? `${remainingDays} ${dayText}` : "";

    if (monthPart && dayPart) {
      return `${monthPart} ${this.translate.instant("bills.and")} ${dayPart}`;
    }
    else if (monthPart) {
      return monthPart;
    }
    else if (dayPart) {
      return dayPart;
    }
    else {
      return "";
    }
  }

  calculateDateDifference(date1: any, date2: any): number {
    const start = dayjs(date1);
    const end = dayjs(date2);
    return end.diff(start, "days");
  }

  openAddModal() {
    const billWithHighestEndDate = this.getLastDate();
    const lastEndDate = billWithHighestEndDate.endDate;
    this.initializeForms(lastEndDate);
    this.addTaskModal = this.dialog.open(this.addTaskModalTemplate, {
      id: `dialog-${crypto.randomUUID()}`,
    });
  }

  onPortalAttached() {
    setTimeout(() => {
      this.positioningService.setPosition(this.dropdownPlaceholder, this.messagePortalComponent);
    }, 0);
  }

  openDeleteModal(billId: number) {
    this.billToDelete = billId;
    this.deleteModal = this.dialog.open(this.deleteConfirmationTemplate, {
      id: `dialog-${crypto.randomUUID()}`,
    });
  }

  closeDeleteModal() {
    this.deleteModal.close();
  }

  handleDelete() {
    if (this.billToDelete) {
      this.isLoadingDelete = true;
      const deleteSubscription = this.billService.deleteBillById(this.billToDelete).subscribe({
        next: () => {
          deleteSubscription.unsubscribe();
          this.isLoadingDelete = false;
          showMessage(PopupType.Success, this.translate.instant("billing.successfull_delete"));
          this.closeDeleteModal();
          this.loadData();
        },
        error: (err) => {
          deleteSubscription.unsubscribe();
          this.isLoadingDelete = false;
          showMessage(PopupType.Danger, err.error);
        },
      });
    }
  };
}
